import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { formatEUR, formatDate } from "@/lib/constants";
import { TICKET_STATUS } from "@/lib/tickets";
import {
  House, CurrencyEur, Ticket, ArrowRight, MapPin, FileText,
} from "@phosphor-icons/react";

export function PortalHome() {
  const { tenancy } = useOutletContext();
  const [ledger, setLedger] = useState(null);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    api.get("/portal/ledger").then((r) => setLedger(r.data)).catch(() => {});
    api.get("/tickets").then((r) => setTickets(r.data)).catch(() => {});
  }, []);

  const t = tenancy?.tenancy;
  const p = tenancy?.property;
  const u = tenancy?.unit;

  return (
    <div className="space-y-6">
      {/* Welcome card */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-md p-6 md:p-8">
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Mieterportal</div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Willkommen bei Domivus</h1>
        {t && (
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-200">
            <span className="flex items-center gap-1.5"><MapPin size={14} /> {p?.address?.street} {p?.address?.house_number}, {p?.address?.zip} {p?.address?.city}</span>
            <span className="flex items-center gap-1.5 font-mono">{u?.unit_number}</span>
            <span className="text-slate-400">·</span>
            <span className="font-mono text-slate-300">{t.contract_number}</span>
          </div>
        )}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          icon={<CurrencyEur size={18} />}
          label="Mietkonto-Saldo"
          value={formatEUR(ledger?.balance ?? 0)}
          accent={ledger?.balance >= 0 ? "text-emerald-700" : "text-rose-700"}
          to="/portal/mietkonto"
          sub={ledger?.balance >= 0 ? "Im Plus" : "Rückstand"}
        />
        <KpiCard
          icon={<Ticket size={18} />}
          label="Offene Tickets"
          value={tickets.filter((x) => !["done", "closed"].includes(x.status)).length}
          to="/portal/tickets"
          sub={`${tickets.length} insgesamt`}
        />
        <KpiCard
          icon={<House size={18} />}
          label="Kaltmiete"
          value={formatEUR(t?.rent_cold ?? 0)}
          to="/portal/mietkonto"
          sub={t ? `Fälligkeit zum ${t.due_day}.` : "—"}
        />
      </div>

      {/* Recent tickets */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Meine letzten Tickets</h2>
          <Link to="/portal/tickets" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-0.5">
            Alle ansehen <ArrowRight size={12} />
          </Link>
        </div>
        <div className="divide-y divide-slate-100">
          {tickets.length === 0 && <div className="px-5 py-6 text-sm text-slate-500">Sie haben noch keine Tickets eingereicht.</div>}
          {tickets.slice(0, 5).map((t) => {
            const s = TICKET_STATUS[t.status];
            return (
              <Link to="/portal/tickets" key={t.id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50">
                <div>
                  <div className="text-sm font-medium text-slate-900">{t.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono">{t.ticket_number} · {formatDate(t.created_at)}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.className}`}>{s.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, accent = "text-slate-900", to }) {
  return (
    <Link to={to || "#"} className="block bg-white border border-slate-200 rounded-md p-4 hover:border-slate-400 transition-colors">
      <div className="flex items-center justify-between text-slate-500 mb-2">
        <div className="text-[10px] uppercase tracking-wider font-semibold">{label}</div>
        {icon}
      </div>
      <div className={`text-2xl font-semibold tracking-tight tabular-nums ${accent}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </Link>
  );
}
